import { type MeResponse, type PermissionCode, isAdminRole, roleHasGmsAccess } from "@seagames/contracts";

/**
 * Who sees what on the Arrival & Departure page. NONE of this decides
 * anything: every read is scoped in SQL (`cardInScope`) and every write is
 * re-checked by the API. It only picks which pages, buttons and panels to
 * draw, so nobody clicks into a page of 403s.
 *
 * A group's permission counts only while the active role is the group's and
 * the group is active (`roleHasGmsAccess`), like everywhere else.
 */

/** The permissions that open the A&D page (nav + route guard). */
export const TRAVEL_PAGE_PERMISSIONS = ["logistics.travel", "logistics.travel_review"] as const satisfies readonly PermissionCode[];

type Session = Pick<MeResponse, "active_role" | "group">;

/** The active group grants `permission`. */
export function groupGrants(me: Session, permission: PermissionCode): boolean {
	return roleHasGmsAccess(me.active_role, me.group) && !!me.group?.permissions.includes(permission);
}

/** The page itself: admins, or a group with either travel permission. */
export const canSeeTravel = (me: Session): boolean => isAdminRole(me.active_role) || TRAVEL_PAGE_PERMISSIONS.some((p) => groupGrants(me, p));

/** "Add travel" for many records at once (`POST /travel/bulk`). */
export const canWriteTravel = (me: Session): boolean => isAdminRole(me.active_role) || groupGrants(me, "logistics.travel");

/** The host desk: override a flight, set a delay, resume tracking. */
export const canReviewTravel = (me: Session): boolean => isAdminRole(me.active_role) || groupGrants(me, "logistics.travel_review");

/**
 * The manual delay of a coach / ferry / untracked-flight group
 * (`POST /travel/delay`): the host desk, or a travel desk over its own
 * cards. The API decides per leg (scope, permission, closed cards).
 */
export const canDelayTravel = (me: Session): boolean =>
	isAdminRole(me.active_role) || groupGrants(me, "logistics.travel_review") || groupGrants(me, "logistics.travel");
